import React from "react";
import PropTypes from "prop-types";
import { Button } from "react-bootstrap";
import FadeIn from "react-fade-in/lib/FadeIn";

import { useContent } from "./hooks";
import ContentOverviewSkeleton from "./components/ContentOverviewSkeleton";
import Countdown from "./components/Countdown";
import TooltipRating from "./components/TooltipRating";
import RatingContainer from "./components/RatingContainer";

import { formattedSportFlights } from "../../utils/dateFnsUtils";
import { getContentImageByMode, getUrlUnityImageApi } from "../../utils/imageUtils";
import { timeHr, timeMin, isContentLive } from "../../utils/contentOverviewUtils";

import Image, { LOADING_IMG } from '../../HOCs/ImageHocs';
import WithErrorBoundary from "../../HOCs/WithErrorBoundary";

import { CONTENT_PROPS, IMAGE_CROP_MODES } from '../shared/constants';
import { IMAGE_MODE } from '../../constants/imageMode';
import SchedulePopover from '../BannerHero/components/SchedulePopover';

import "./styles.scss";

const ContentOverviewFuture = ({
  content,
  isLoading,
  onClickPlayerButton,
  contentNoImageProps,
  translationTexts,
  ratingsList,
  ratingImages,
  mustShowRatingImage = false,
  showPlayIcon = true,
  backgroundImage,
  redirectContent = false,
  networks,
  children,
  showRating = true,
  showChannel = true,
  showYear = true,
  showDuration = true,
  showLive = true,
  showScheduleButton = true
}) => {
  const now = new Date();
  const {
    title,
    ratings,
    release,
    duration,
    endFlight = `${now.getFullYear() + 1}-01-01T00:00:00.000Z`,
    start,
    sport,
    arrayFlightsStartEnd
  } = useContent(content);

  const {
    content_classification,
    play_now,
    play_in,
    content_not_available,
    live_on,
    at,
    available_live_on,
    live,
    schedule,
    share_on
  } = translationTexts ?? {};

  const contentNoImagePropsR = {
    ...contentNoImageProps,
    backgroundColorOne: contentNoImageProps?.backgroundColorTwo,
    backgroundColorTwo: contentNoImageProps?.backgroundColorOne
  };

  const thumbBackground = !isLoading && getContentImageByMode(content?.images, "THUMB")?.url;

  const nowTime = now.getTime();
  const startTime = start ? new Date(start).getTime() : nowTime - 1;
  const endTime = new Date(endFlight).getTime();
  const [ dateEvent, timeEvent ] = formattedSportFlights(arrayFlightsStartEnd);

  const [days, setDays] = React.useState();
  const [hours, setHours] = React.useState();
  const [minutes, setMinutes] = React.useState();
  const [seconds, setSeconds] = React.useState();
  const [isLive, setIsLive] = React.useState(
    isContentLive(start, endFlight)
  );

  // COUNTDOWN EVERY 1 SECOND
  const countdown = () => {
    const interval = setInterval(function() {
      const now = new Date().getTime();
      const until = startTime - now;
    
      setDays(Math.floor(
        until / (1000 * 60 * 60 * 24)
      ));
  
      setHours(Math.floor(
        (until % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      ));
  
      setMinutes(Math.floor(
        (until % (1000 * 60 * 60)) / (1000 * 60)
      ));
  
      setSeconds(Math.floor(
        (until % (1000 * 60)) / 1000)
      );

      setIsLive(( days === 0 && hours === 0 && minutes === 0 && seconds === 0) && isContentLive(start, endFlight));

      if (until < 0) {
        setIsLive(true);
        clearInterval(interval);
      }
    }, 1000);

    return interval;
  }

  const checkStatusContent = () => {
    const interval = setInterval(() => {
      const isLive = isContentLive(start, endFlight);
      if ( !isLive ) {
        setIsLive(isLive);
        clearInterval(interval);
      };
    }, 10000);

    return interval;
  }

  React.useEffect(() => {
    let interval;
  
    if ( startTime > nowTime )
      interval = countdown();

    if ( isLive )
      interval = checkStatusContent();

    return () => interval && clearInterval(interval);
  }, [isLoading]);

  return (
    <React.Fragment>
      <div className="contentOverviewContainer">
        <figure className="contentOverviewBackground">
          {!isLoading && 
            <Image
              crop={IMAGE_CROP_MODES.resizeScaleAndCrop}
              loading={LOADING_IMG.EAGER}
              size={IMAGE_MODE.THUMB_OVERVIEW}
              src={backgroundImage}
            />
          }
        </figure>

        <div className="contentOverviewWrapper">
        {isLoading ? 
          <ContentOverviewSkeleton
            backgroundColor={contentNoImagePropsR?.backgroundColorOne}
            foregroundColor={contentNoImagePropsR?.backgroundColorTwo}
            speed={2}
          /> : 
          <FadeIn>
            <div className="contentOverviewHeader">
              <Button
                className="contentOverviewExitButton"
                onClick={() => window.history.go(-1)}
              >
                <i className="bi bi-arrow-left-short"></i>
              </Button>
            </div>

            <div className="contentOverviewDetail">
              <div className="contentOverviewThumb">
                <figure className="tbxContentCard__content-image tbxContentCard__aspect-ratio">
                  <Image
                    {...{
                      className: 'tbxContentCard__image',
                      alt: title,
                      src: getUrlUnityImageApi(thumbBackground),
                      crop: IMAGE_CROP_MODES.resizeScaleAndCrop,
                      size: IMAGE_MODE.THUMB,
                      loading: 'lazy',
                      width: '100%',
                      height: '100%'
                    }}
                  />
                </figure>
              </div>

              <div className="contentOverviewInfo">
                <div className="contentOverviewTitle">
                  <h2>
                    <span className="league">{ sport?.league }</span>
                    <span className="title">{ title }</span>
                  </h2>
                </div>

                {(showLive || showChannel || showDuration || showRating || showYear) && (
                  <div className="contentOverviewPeriod">
                    <div className="contentOverviewCategory">

                      {
                        showRating && (<>
                          {!!ratings && ratingsList ? 
                            <TooltipRating
                              ratings={ratings}
                              ratingsList={ratingsList}
                              title={content_classification}
                            /> : 
                            <RatingContainer
                              ratings={ratings}
                              ratingImages={ratingImages}
                              mustShowRatingImage={mustShowRatingImage}
                            />
                          }
                        </>)
                      }
                      

                      {showChannel && !!content?.networks[0] && 
                        <p className="contentOverviewNetwork">
                          {content.networks[0]?.name}
                        </p>
                      }

                      {showYear && !isLive && !!release && 
                        <p className="contentOverviewYear">
                          {release}
                        </p>
                      }

                      {showDuration && !isLive && !!duration && 
                        <div className="contentOverviewLength">
                          {!!timeHr(duration) && 
                            <p className="contentOverviewHr">
                              <span className="contentOverviewHrNumber">
                                {timeHr(duration)}
                              </span>
                              <span className="contentOverviewHrText">
                                hs
                              </span>
                            </p>
                          }

                          {!!timeMin(duration) && 
                            <p className="contentOverviewMin">
                              <span className="contentOverviewMinNumber">
                                {timeMin(duration)}
                              </span>
                              <span className="contentOverviewMinText">
                                min
                              </span>
                            </p>
                          }
                        </div>
                      }

                      {showLive && isLive &&
                        <p className="contentOverViewLiveLabel">
                          <i className="bi bi-broadcast"></i> 
                          <span> {live}</span>
                        </p>
                      }
                    </div>
                  </div>
                )}

                { React.isValidElement(children) &&  
                  <div className="contentOverviewFutureOptions">
                    {children}
                   </div>
                }  

                <div className="contentOverviewActions">
                  <div className="contentOverviewActionsContainerPlay">
                    {isLive && 
                      <Button
                        className={`contentOverviewActionsPlay ${!showPlayIcon ? 'no-icon' : ''}`}
                        onClick={() => onClickPlayerButton(content?.id)}
                      >
                        {showPlayIcon && 
                          <i className="bi bi-play-fill"></i>
                        }

                        <span className="contentOverviewActionsPlayTitle">
                          {redirectContent ? `${play_in}: ${content.networks?.map((network) => network.name).join(', ')}` : play_now}
                        </span>
                      </Button>
                    }

                    {!isLive && startTime > nowTime &&
                      <React.Fragment>
                        <Countdown
                          date={{days, hours, minutes, seconds}}
                          labels={{available_live_on}}
                        />

                        <p className="contentOverviewAvailableBadge">
                          {live_on} {dateEvent} {at} {timeEvent}
                        </p>

                        {showScheduleButton && <SchedulePopover content={content} schedule={schedule} />}
                      </React.Fragment>
                    }

                    {!isLive && endTime < nowTime &&
                      <Button
                        className="contentOverviewActionsDisabled"
                      >
                        { content_not_available }
                      </Button>
                    }
                  </div>
                </div>
              </div>
            </div>
          </FadeIn>
        }
        </div>
      </div>
    </React.Fragment>
  )
}

ContentOverviewFuture.propTypes = {
  content: PropTypes.shape(CONTENT_PROPS),
  isLoading: PropTypes.bool,
  onClickBackButton: PropTypes.func,
  contentNoImageProps: PropTypes.object,
  translationTexts: PropTypes.object,
  ratingsList: PropTypes.array,
  ratingImages: PropTypes.array,
  mustShowRatingImage: PropTypes.bool,
  showPlayIcon: PropTypes.bool,
  showScheduleButton: PropTypes.bool,
  onClickPlayerButton: PropTypes.func,
}

export default WithErrorBoundary(React.memo(ContentOverviewFuture));
